import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { ALLERGEN_TYPES, type AllergenInfo } from '@/types';

interface AllergenSelectorProps {
  value: AllergenInfo;
  onChange: (allergens: AllergenInfo) => void;
  className?: string;
  onFieldFocus?: () => void;
}

/**
 * AllergenSelector - Comprehensive allergen information selector
 * Shows common Indian cuisine allergens with custom allergen support
 */
export const AllergenSelector: React.FC<AllergenSelectorProps> = ({
  value,
  onChange,
  className,
  onFieldFocus,
}) => {
  const [newCustomAllergen, setNewCustomAllergen] = useState('');
  const [newMayContain, setNewMayContain] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [showMayContainInput, setShowMayContainInput] = useState(false);

  const handleAllergenToggle = (allergen: keyof typeof ALLERGEN_TYPES) => {
    const updatedAllergens = { ...value, [allergen]: !value[allergen] };
    onChange(updatedAllergens);
    onFieldFocus?.();
  };

  const handleAddCustomAllergen = () => {
    if (newCustomAllergen.trim() && !value.custom.includes(newCustomAllergen.trim())) {
      const updatedAllergens = {
        ...value,
        custom: [...value.custom, newCustomAllergen.trim()]
      };
      onChange(updatedAllergens);
      setNewCustomAllergen('');
      setShowCustomInput(false);
      onFieldFocus?.();
    }
  };

  const handleRemoveCustomAllergen = (allergen: string) => {
    const updatedAllergens = {
      ...value,
      custom: value.custom.filter(item => item !== allergen)
    };
    onChange(updatedAllergens);
    onFieldFocus?.();
  };

  const handleAddMayContain = () => {
    if (newMayContain.trim() && !value.mayContain.includes(newMayContain.trim())) {
      const updatedAllergens = {
        ...value,
        mayContain: [...value.mayContain, newMayContain.trim()]
      };
      onChange(updatedAllergens);
      setNewMayContain('');
      setShowMayContainInput(false);
      onFieldFocus?.();
    }
  };

  const handleRemoveMayContain = (allergen: string) => {    const updatedAllergens = {
      ...value,
      mayContain: value.mayContain.filter(item => item !== allergen)
    };
    onChange(updatedAllergens);
    onFieldFocus?.();
  };

  const hasSelectedAllergens = Object.keys(ALLERGEN_TYPES).some(key => 
    value[key as keyof typeof ALLERGEN_TYPES]
  ) || value.custom.length > 0 || value.mayContain.length > 0;

  return (
    <div className={cn("space-y-4", className)}>
      <div className="space-y-2">
        {/* Common allergens grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {Object.entries(ALLERGEN_TYPES).map(([key, allergenType]) => (
            <motion.button
              key={key}
              type="button"
              onClick={() => handleAllergenToggle(key as keyof typeof ALLERGEN_TYPES)}
              onFocus={onFieldFocus}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={cn(
                "relative p-3 rounded-lg border-2 transition-all duration-200",
                "flex flex-col items-center text-center space-y-1",
                "hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2",
                value[key as keyof typeof ALLERGEN_TYPES]
                  ? "border-red-500 bg-red-50 text-red-700 focus:ring-red-500/20"
                  : "border-gray-200 bg-white hover:border-gray-300 focus:ring-primary/20"
              )}
            >
              {/* Allergen label */}
              <span className="text-sm font-medium">
                {allergenType.label}
              </span>
              
              {/* Selection indicator */}
              {value[key as keyof typeof ALLERGEN_TYPES] && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold"
                >
                  ✓
                </motion.div>
              )}
            </motion.button>
          ))}
        </div>

        {/* Custom allergens section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-600">Custom Allergens</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowCustomInput(!showCustomInput)}
              className="text-primary00 hover:text-primary"
            >
              Add Custom
            </Button>
          </div>

          {/* Custom allergen input */}
          <AnimatePresence>
            {showCustomInput && (              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="flex space-x-2"
              >
                <input
                  type="text"
                  value={newCustomAllergen}
                  onChange={(e) => setNewCustomAllergen(e.target.value)}
                  placeholder="Enter custom allergen"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCustomAllergen()}
                  className="flex-1 h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-base transition-all duration-200"
                />
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={handleAddCustomAllergen}
                  disabled={!newCustomAllergen.trim()}
                >
                  Add
                </Button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Custom allergen tags */}
          {value.custom.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {value.custom.map((allergen) => (
                <motion.span
                  key={allergen}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0 }}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"
                >
                  {allergen}
                  <button
                    type="button"
                    onClick={() => handleRemoveCustomAllergen(allergen)}
                    className="ml-1 text-red-600 hover:text-red-800"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </motion.span>
              ))}
            </div>
          )}
        </div>

        {/* May contain section */}
        <div className="space-y-2 border-t pt-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4 text-yellow-600" />
              <span className="text-sm font-medium text-gray-600">May Contain (Cross-contamination)</span>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowMayContainInput(!showMayContainInput)}
              className="text-yellow-600 hover:text-yellow-700"
            >
              Add Warning
            </Button>
          </div>

          {/* May contain input */}
          <AnimatePresence>
            {showMayContainInput && (              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="flex space-x-2"
              >
                <input
                  type="text"
                  value={newMayContain}
                  onChange={(e) => setNewMayContain(e.target.value)}
                  placeholder="Enter allergen that may be present"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddMayContain()}
                  className="flex-1 h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-base transition-all duration-200"
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleAddMayContain}
                  disabled={!newMayContain.trim()}
                >
                  Add
                </Button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* May contain tags */}
          {value.mayContain.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {value.mayContain.map((allergen) => (
                <motion.span
                  key={allergen}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0 }}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"
                >
                  {allergen}
                  <button
                    type="button"
                    onClick={() => handleRemoveMayContain(allergen)}
                    className="ml-1 text-yellow-600 hover:text-yellow-800"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </motion.span>
              ))}
            </div>
          )}
        </div>

        {/* Summary */}
        {hasSelectedAllergens && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-gray-50 rounded-lg p-3 border-l-4 border-red-500"
          >
            <p className="text-sm text-gray-700">
              <strong>Allergen Summary:</strong> This item contains allergens that may affect customers with dietary restrictions.
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};